/**
 * Authentication Context Provider
 *
 * Manages authentication state, token storage, and OAuth2 flows.
 * Uses localStorage for token storage.
 */

import {
  useEffect,
  useState,
  useCallback,
  type ReactElement,
  type ReactNode,
} from 'react';
import { authApi } from '../api/authApi';
import { AuthContext } from './authContextDef';
import { STORAGE_KEYS } from '@utils/constants';
import type { AuthContextValue, AuthState } from '../types/auth.types';

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  accessToken: null,
  refreshToken: null,
  oauthEnabled: false,
  isAdmin: false,
  error: null,
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const [state, setState] = useState<AuthState>(initialState);

  const initializeAuth = useCallback(async (): Promise<void> => {
    const handleOAuthCallback = async (
      code: string,
      returnedState: string
    ): Promise<void> => {
      const storedState = sessionStorage.getItem(STORAGE_KEYS.AUTH_STATE);
      const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);

      if (returnedState !== storedState) {
        throw new Error('State mismatch - possible CSRF attack');
      }

      if (!codeVerifier) {
        throw new Error('Code verifier not found');
      }

      // Exchange code for tokens
      const tokens = await authApi.exchangeCode(code, returnedState, codeVerifier);

      // Store tokens
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
      }

      // Clean up session storage
      sessionStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
      sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);

      // Get user info
      const status = await authApi.getStatus();

      setState({
        isAuthenticated: true,
        isLoading: false,
        user: status.user,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token ?? null,
        oauthEnabled: true,
        isAdmin: status.is_admin,
        error: null,
      });
    };

    try {
      // Check for OAuth callback params in URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const returnedState = urlParams.get('state');

      if (code && returnedState) {
        await handleOAuthCallback(code, returnedState);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      // Check for stored tokens
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      // Get auth status from backend
      const status = await authApi.getStatus();

      // If OAuth is not enabled (dev bypass mode), mark as authenticated
      if (!status.oauth_enabled) {
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: status.user,
          accessToken: null,
          refreshToken: null,
          oauthEnabled: false,
          isAdmin: status.is_admin,
          error: null,
        });
        return;
      }

      // OAuth is enabled - check if we have valid tokens
      if (accessToken && status.authenticated && status.user) {
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: status.user,
          accessToken,
          refreshToken,
          oauthEnabled: true,
          isAdmin: status.is_admin,
          error: null,
        });
        return;
      }

      // Not authenticated
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        oauthEnabled: status.oauth_enabled,
        isAdmin: false,
        error: null,
      });
    } catch (error) {
      console.error('Auth initialization failed:', error);
      setState({
        ...initialState,
        isLoading: false,
        error: 'Failed to initialize authentication',
      });
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    void initializeAuth();
  }, [initializeAuth]);

  const login = useCallback(async (): Promise<void> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const { url, state: authState, code_verifier } = await authApi.getLoginUrl();

      // Store PKCE parameters in session storage
      sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, code_verifier);
      sessionStorage.setItem(STORAGE_KEYS.AUTH_STATE, authState);

      // Redirect to OAuth provider
      window.location.href = url;
    } catch (error) {
      console.error('Login failed:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Failed to initiate login',
      }));
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      const response = await authApi.logout();

      // Clear stored tokens
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

      // Reset state
      setState((prev) => ({
        ...initialState,
        isLoading: false,
        oauthEnabled: prev.oauthEnabled,
      }));

      // Redirect to Keycloak logout if URL provided
      if (response.logout_url) {
        window.location.href = response.logout_url;
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear local state even if API call fails
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      setState((prev) => ({
        ...initialState,
        isLoading: false,
        oauthEnabled: prev.oauthEnabled,
      }));
    }
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<void> => {
    const refreshToken = state.refreshToken;
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const tokens = await authApi.refreshToken(refreshToken);

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
      }

      setState((prev) => ({
        ...prev,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token ?? prev.refreshToken,
      }));
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Force re-login
      await logout();
      throw error;
    }
  }, [state.refreshToken, logout]);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
